import { useMemo, Fragment } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { safeParseDecimal } from "@/lib/excel";
import Decimal from "decimal.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SummaryInfo } from "@/lib/api/directSales";

// Helper function to format numbers
const formatNumber = (value: Decimal | string | number | undefined | null, fractionDigits = 2): string => {
  const decimalValue = safeParseDecimal(value || 0);
  if (decimalValue.isNaN()) {
    return "N/A";
  }
  return decimalValue.toFixed(fractionDigits).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// Helper function to format currency
const formatCurrency = (value: Decimal | string | number | undefined | null): string => {
  const decimalValue = safeParseDecimal(value || 0);
  if (decimalValue.isNaN()) {
    return "N/A";
  }
  const formatted = decimalValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `$${formatted}`;
};

// Type for the processed hierarchical data
type PlaceholderStatusSummaryData = {
  total_qty: Decimal;
  total_marketing_fee: Decimal;
  statuses: Record<"open" | "closed", {
    total_qty: Decimal;
    total_marketing_fee: Decimal;
    years: Record<number, {
      total_qty: Decimal;
      total_marketing_fee: Decimal;
      months: Record<string, {
        month_display: string;
        total_qty: Decimal;
        total_marketing_fee: Decimal;
      }>;
      sortedMonths: string[];
    }>;
    sortedYears: number[];
  }>;
}

export function SummaryTable({ summary }: { summary: SummaryInfo }) {
  // Calculate totals using useMemo
  const grandTotalQty = useMemo(() =>
    summary.yearSummary.reduce((acc, curr) => acc.plus(safeParseDecimal(curr.total_qty)), new Decimal(0)),
    [summary.yearSummary]
  );
  const grandTotalFee = useMemo(() =>
    summary.yearSummary.reduce((acc, curr) => acc.plus(safeParseDecimal(curr.total_marketing_fee)), new Decimal(0)),
    [summary.yearSummary]
  );

  // Calculate totals grouped by has_placeholder -> status -> year -> month
  const placeholderBreakdown = useMemo((): Record<"true" | "false", PlaceholderStatusSummaryData> => {
    const result: Record<"true" | "false", PlaceholderStatusSummaryData> = {
      "true": {
        total_qty: new Decimal(0),
        total_marketing_fee: new Decimal(0),
        statuses: {
          open: { total_qty: new Decimal(0), total_marketing_fee: new Decimal(0), years: {}, sortedYears: [] },
          closed: { total_qty: new Decimal(0), total_marketing_fee: new Decimal(0), years: {}, sortedYears: [] },
        }
      },
      "false": {
        total_qty: new Decimal(0),
        total_marketing_fee: new Decimal(0),
        statuses: {
          open: { total_qty: new Decimal(0), total_marketing_fee: new Decimal(0), years: {}, sortedYears: [] },
          closed: { total_qty: new Decimal(0), total_marketing_fee: new Decimal(0), years: {}, sortedYears: [] },
        }
      },
    };

    // Process Year Summaries first to get placeholder and status totals and initialize years
    summary.yearSummary.forEach(item => {
      const hasPlaceholder = item.has_placeholder_data ? "true" : "false";
      const status = item.status;
      const year = item.year;
      const qty = safeParseDecimal(item.total_qty);
      const fee = safeParseDecimal(item.total_marketing_fee);

      result[hasPlaceholder].total_qty = result[hasPlaceholder].total_qty.plus(qty);
      result[hasPlaceholder].total_marketing_fee = result[hasPlaceholder].total_marketing_fee.plus(fee);

      result[hasPlaceholder].statuses[status].total_qty = result[hasPlaceholder].statuses[status].total_qty.plus(qty);
      result[hasPlaceholder].statuses[status].total_marketing_fee = result[hasPlaceholder].statuses[status].total_marketing_fee.plus(fee);

      if (!result[hasPlaceholder].statuses[status].years[year]) {
        result[hasPlaceholder].statuses[status].years[year] = { total_qty: new Decimal(0), total_marketing_fee: new Decimal(0), months: {}, sortedMonths: [] };
      }
      // Accumulate year totals directly here
      result[hasPlaceholder].statuses[status].years[year].total_qty = result[hasPlaceholder].statuses[status].years[year].total_qty.plus(qty);
      result[hasPlaceholder].statuses[status].years[year].total_marketing_fee = result[hasPlaceholder].statuses[status].years[year].total_marketing_fee.plus(fee);
    });

    // Process Month Summaries
    summary.monthSummary.forEach(item => {
      const hasPlaceholder = item.has_placeholder_data ? "true" : "false";
      const status = item.status;
      const year = item.year;
      const month = item.month;
      const month_display = item.month_display;
      const qty = safeParseDecimal(item.total_qty);
      const fee = safeParseDecimal(item.total_marketing_fee);
      const monthKey = `${year}-${month.toString().padStart(2, '0')}`;

      // Ensure year exists (it should from yearSummary processing)
      if (!result[hasPlaceholder].statuses[status].years[year]) {
        result[hasPlaceholder].statuses[status].years[year] = { total_qty: new Decimal(0), total_marketing_fee: new Decimal(0), months: {}, sortedMonths: [] };
      }

      if (!result[hasPlaceholder].statuses[status].years[year].months[monthKey]) {
        result[hasPlaceholder].statuses[status].years[year].months[monthKey] = { month_display: month_display, total_qty: new Decimal(0), total_marketing_fee: new Decimal(0) };
      }

      result[hasPlaceholder].statuses[status].years[year].months[monthKey].total_qty = result[hasPlaceholder].statuses[status].years[year].months[monthKey].total_qty.plus(qty);
      result[hasPlaceholder].statuses[status].years[year].months[monthKey].total_marketing_fee = result[hasPlaceholder].statuses[status].years[year].months[monthKey].total_marketing_fee.plus(fee);
    });

    // Sort years and months within each placeholder/status combination
    (['true', 'false'] as const).forEach(hasPlaceholder => {
      (['open', 'closed'] as const).forEach(status => {
        result[hasPlaceholder].statuses[status].sortedYears = Object.keys(result[hasPlaceholder].statuses[status].years).map(Number).sort((a, b) => a - b);
        result[hasPlaceholder].statuses[status].sortedYears.forEach(year => {
          result[hasPlaceholder].statuses[status].years[year].sortedMonths = Object.keys(result[hasPlaceholder].statuses[status].years[year].months).sort((a, b) => a.localeCompare(b));
        });
      });
    });

    return result;
  }, [summary.yearSummary, summary.monthSummary]);


  return (
    <section className="grid gap-4 grid-cols-1 md:grid-cols-2">
      {/* Card 1: Placeholder and Status Breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Totals by Placeholder and Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(['true', 'false'] as const).map(hasPlaceholder => {
            const placeholderData = placeholderBreakdown[hasPlaceholder];
            if (placeholderData.total_qty.eq(0)) return null; // Don't render if no data
            return (
              <div key={hasPlaceholder} className="space-y-2">
                <h3 className="font-semibold text-sm border-b pb-1">
                  {hasPlaceholder === 'true' ? 'With Placeholder Data' : 'Without Placeholder Data'}
                </h3>
                {(['open', 'closed'] as const).map(status => {
                  const statusData = placeholderData.statuses[status];
                  if (statusData.total_qty.eq(0)) return null;
                  return (
                    <div key={`${hasPlaceholder}-${status}`} className="p-2 bg-muted rounded-md ml-2">
                      <h4 className="font-medium mb-1 uppercase text-xs">{status}</h4>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-600">Total Bushel Quantity:</span>
                        <span className="tabular-nums">{formatNumber(statusData.total_qty)}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-600">Total Marketing Fee:</span>
                        <span className="tabular-nums">{formatCurrency(statusData.total_marketing_fee)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
          {grandTotalQty.eq(0) && (
            <p className="text-sm text-muted-foreground text-center">No data available.</p>
          )}
        </CardContent>
      </Card>

      {/* Card 2: Placeholder -> Status -> Year Breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Totals by Placeholder, Status, and Year</CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="compact-table">
            <TableHeader>
              <TableRow className="h-8">
                <TableHead className="py-1">Placeholder / Status / Year</TableHead>
                <TableHead className="py-1 text-right">Total Bushel Qty</TableHead>
                <TableHead className="py-1 text-right">Total Marketing Fee</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(['true', 'false'] as const).map(hasPlaceholder => {
                const placeholderData = placeholderBreakdown[hasPlaceholder];
                if (placeholderData.total_qty.eq(0)) return null;

                return (
                  <Fragment key={`placeholder-year-${hasPlaceholder}`}>
                    {/* Placeholder Header Row */}
                    <TableRow className="h-7 text-sm bg-blue-100 dark:bg-blue-900">
                      <TableCell className="py-1 font-bold">
                        {hasPlaceholder === 'true' ? 'With Placeholder Data' : 'Without Placeholder Data'}
                      </TableCell>
                      <TableCell className="py-1 text-right tabular-nums font-bold">{formatNumber(placeholderData.total_qty)}</TableCell>
                      <TableCell className="py-1 text-right tabular-nums font-bold">{formatCurrency(placeholderData.total_marketing_fee)}</TableCell>
                    </TableRow>

                    {/* Status Rows */}
                    {(['open', 'closed'] as const).map(status => {
                      const statusData = placeholderData.statuses[status];
                      if (statusData.total_qty.eq(0)) return null;

                      return (
                        <Fragment key={`${hasPlaceholder}-status-year-${status}`}>
                          {/* Status Header Row */}
                          <TableRow className="h-7 text-sm bg-gray-200 dark:bg-gray-700">
                            <TableCell className="py-1 pl-4 font-semibold uppercase">{status}</TableCell>
                            <TableCell className="py-1 text-right tabular-nums font-semibold">{formatNumber(statusData.total_qty)}</TableCell>
                            <TableCell className="py-1 text-right tabular-nums font-semibold">{formatCurrency(statusData.total_marketing_fee)}</TableCell>
                          </TableRow>

                          {/* Year Rows */}
                          {statusData.sortedYears.map(year => {
                            const yearData = statusData.years[year];
                            if (yearData.total_qty.eq(0)) return null;

                            return (
                              <TableRow key={`${hasPlaceholder}-${status}-year-${year}`} className="h-6 text-xs">
                                <TableCell className="py-0.5 pl-8">{year}</TableCell>
                                <TableCell className="py-0.5 text-right tabular-nums">{formatNumber(yearData.total_qty)}</TableCell>
                                <TableCell className="py-0.5 text-right tabular-nums">{formatCurrency(yearData.total_marketing_fee)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                  </Fragment>
                );
              })}
              {grandTotalQty.eq(0) && (
                <TableRow>
                  <TableCell colSpan={3} className="h-24 text-center">
                    No data available.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>


      {/* Card 3: Placeholder -> Status -> Year -> Month Breakdown */}
      <Card className="col-span-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Totals by Placeholder, Status, Year, and Month</CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="compact-table">
            <TableHeader>
              <TableRow className="h-8">
                <TableHead className="py-1">Placeholder / Status / Year / Month</TableHead>
                <TableHead className="py-1 text-right">Total Bushel Qty</TableHead>
                <TableHead className="py-1 text-right">Total Marketing Fee</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(['true', 'false'] as const).map(hasPlaceholder => {
                const placeholderData = placeholderBreakdown[hasPlaceholder];
                if (placeholderData.total_qty.eq(0)) return null;

                return (
                  <Fragment key={`placeholder-month-${hasPlaceholder}`}>
                    {/* Placeholder Header Row */}
                    <TableRow className="h-7 text-sm bg-blue-100 dark:bg-blue-900">
                      <TableCell className="py-1 font-bold">
                        {hasPlaceholder === 'true' ? 'With Placeholder Data' : 'Without Placeholder Data'}
                      </TableCell>
                      <TableCell className="py-1 text-right tabular-nums font-bold">{formatNumber(placeholderData.total_qty)}</TableCell>
                      <TableCell className="py-1 text-right tabular-nums font-bold">{formatCurrency(placeholderData.total_marketing_fee)}</TableCell>
                    </TableRow>

                    {/* Status Rows */}
                    {(['open', 'closed'] as const).map(status => {
                      const statusData = placeholderData.statuses[status];
                      if (statusData.total_qty.eq(0)) return null;

                      return (
                        <Fragment key={`${hasPlaceholder}-status-month-${status}`}>
                          {/* Status Header Row */}
                          <TableRow className="h-7 text-sm bg-gray-200 dark:bg-gray-700">
                            <TableCell className="py-1 pl-4 font-semibold uppercase">{status}</TableCell>
                            <TableCell className="py-1 text-right tabular-nums font-semibold">{formatNumber(statusData.total_qty)}</TableCell>
                            <TableCell className="py-1 text-right tabular-nums font-semibold">{formatCurrency(statusData.total_marketing_fee)}</TableCell>
                          </TableRow>

                          {/* Year Rows */}
                          {statusData.sortedYears.map(year => {
                            const yearData = statusData.years[year];
                            if (yearData.total_qty.eq(0)) return null;

                            return (
                              <Fragment key={`${hasPlaceholder}-${status}-year-month-${year}`}>
                                <TableRow className="h-6 text-xs bg-muted/50">
                                  <TableCell className="py-0.5 pl-8 font-bold">{year}</TableCell>
                                  <TableCell className="py-0.5 text-right tabular-nums font-medium">{formatNumber(yearData.total_qty)}</TableCell>
                                  <TableCell className="py-0.5 text-right tabular-nums font-medium">{formatCurrency(yearData.total_marketing_fee)}</TableCell>
                                </TableRow>

                                {/* Month Rows */}
                                {yearData.sortedMonths.map(monthKey => {
                                  const monthData = yearData.months[monthKey];
                                  if (monthData.total_qty.eq(0)) return null;

                                  return (
                                    <TableRow key={`${hasPlaceholder}-${status}-month-${monthKey}`} className="h-6 text-xs">
                                      <TableCell className="py-0.5 pl-14">{monthData.month_display}</TableCell>
                                      <TableCell className="py-0.5 text-right tabular-nums">{formatNumber(monthData.total_qty)}</TableCell>
                                      <TableCell className="py-0.5 text-right tabular-nums">{formatCurrency(monthData.total_marketing_fee)}</TableCell>
                                    </TableRow>
                                  );
                                })}
                              </Fragment>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                  </Fragment>
                );
              })}
              {grandTotalQty.eq(0) && (
                <TableRow>
                  <TableCell colSpan={3} className="h-24 text-center">
                    No data available.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </section>
  );
}
